// ══════════════════════════════════════════════════════
// Trade-Analysis Charts — SVG-Komponenten (Phase 2a)
// Konsumiert den stats-Output des `aggregate_trade_stats`-Backend-Helpers.
// Keine externen Libs — Inline-SVG, passend zum bestehenden Chart-Stil.
// ══════════════════════════════════════════════════════

import { useState, useRef } from "react";
import { theme, fonts } from "../theme";

// ── Helper: compact dollar format ─────────────────────────
const dollar = (v, d = 0) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 10000) return `${n < 0 ? "-" : ""}$${(abs / 1000).toFixed(1)}k`;
  return `${n < 0 ? "-" : ""}$${abs.toFixed(d)}`;
};
const fmtPct = (v, d = 1) => (v == null || isNaN(v)) ? "—" : `${Number(v).toFixed(d)}%`;
const fmtNum = (v, d = 2) => (v == null || isNaN(v)) ? "—" : Number(v).toFixed(d);

// ═════════════════════════════════════════════════════════
// SUMMARY METRICS — Grid aus Kennzahlen (3 Spalten)
// ═════════════════════════════════════════════════════════
export function SummaryMetrics({ summary }) {
  if (!summary) return null;
  // PF: null = infinity (kein einziger Verlust)
  const pfLabel = summary.profit_factor == null ? "∞" : fmtNum(summary.profit_factor);
  const metrics = [
    { label: "Total P&L", value: dollar(summary.total_pnl, 0),
      color: summary.total_pnl >= 0 ? theme.greenText : theme.redText, big: true },
    { label: "Trades", value: summary.total_trades,
      sub: `${summary.winners}W · ${summary.losers}L${summary.breakeven ? ` · ${summary.breakeven}BE` : ""}` },
    { label: "Win-Rate", value: fmtPct(summary.win_rate) },
    { label: "Profit Factor", value: pfLabel,
      color: summary.profit_factor >= 1.5 ? theme.greenText : summary.profit_factor >= 1.0 ? theme.amberText : theme.redText },
    { label: "Expectancy", value: dollar(summary.expectancy, 2),
      color: summary.expectancy >= 0 ? theme.greenText : theme.redText, sub: "per trade" },
    { label: "Max DD (Trade-Equity)", value: dollar(summary.max_dd_trade_usd, 0),
      sub: fmtPct(summary.max_dd_trade_pct), color: theme.redText },
    { label: "Avg Win / Loss", value: `${dollar(summary.avg_win)} / ${dollar(summary.avg_loss)}`,
      sub: `best ${dollar(summary.largest_win)} · worst ${dollar(summary.largest_loss)}` },
    { label: "Avg MAE / MFE", value: `${dollar(summary.avg_mae)} / ${dollar(summary.avg_mfe)}`,
      sub: "adverse / favorable excursion" },
  ];
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: 10,
    }}>
      {metrics.map((m, i) => (
        <div key={i} style={{
          padding: "10px 14px",
          background: theme.white,
          border: `1px solid ${theme.border}`,
          borderRadius: 10,
        }}>
          <div style={{ fontSize: 10, color: theme.text3, textTransform: "uppercase",
                        letterSpacing: "0.4px", fontWeight: 700, marginBottom: 4 }}>
            {m.label}
          </div>
          <div style={{ fontSize: m.big ? 22 : 16, fontWeight: 800,
                        fontFamily: fonts.mono, color: m.color || theme.text,
                        letterSpacing: "-0.3px" }}>
            {m.value}
          </div>
          {m.sub && <div style={{ fontSize: 10, color: theme.text4,
                                   fontFamily: fonts.mono, marginTop: 2 }}>{m.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// TRADE EQUITY CURVE — kumulative PnL-Kurve mit Hover-Tooltip
// ═════════════════════════════════════════════════════════
export function TradeEquityCurve({ points, W = 720, H = 220 }) {
  const [tip, setTip] = useState(null);
  const ref = useRef(null);
  if (!points || points.length < 2) {
    return <div style={{
      height: H, display: "flex", alignItems: "center", justifyContent: "center",
      color: theme.text4, fontSize: 12, fontStyle: "italic",
    }}>Nicht genug Trades für Equity-Kurve</div>;
  }

  const values = points.map(p => Number(p.cumulative_pnl) || 0);
  const mn = Math.min(0, ...values);
  const mx = Math.max(0, ...values);
  const rng = mx - mn || 1;
  const P = { t: 18, r: 16, b: 28, l: 62 };
  const iw = W - P.l - P.r, ih = H - P.t - P.b;

  const xy = (i, v) => [
    P.l + (i / (points.length - 1)) * iw,
    P.t + ih - ((v - mn) / rng) * ih,
  ];
  const pts = values.map((v, i) => xy(i, v));
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ");
  const zeroY = P.t + ih - ((0 - mn) / rng) * ih;
  const last = pts[pts.length - 1];
  const up = values[values.length - 1] >= 0;
  const color = up ? "#16A34A" : "#DC2626";

  const gridLines = 4;
  const grids = Array.from({ length: gridLines + 1 }, (_, i) => {
    const v = mn + (rng * i) / gridLines;
    return { y: P.t + ih - ((v - mn) / rng) * ih, label: dollar(v, 0) };
  });

  const onMove = e => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const sx = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.max(0, Math.min(points.length - 1,
      Math.round(((sx - P.l) / iw) * (points.length - 1))));
    const p = points[idx];
    setTip({
      x: pts[idx][0] * (rect.width / W),
      y: pts[idx][1] * (rect.width / W) * (H / W),  // bisschen ungenau, reicht
      clientX: e.clientX - rect.left,
      clientY: e.clientY - rect.top,
      val: values[idx],
      timestamp: p.timestamp,
      trade_id: p.trade_id,
      idx,
    });
  };

  return (
    <div ref={ref} style={{ position: "relative" }}
         onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
      {tip && (
        <div style={{
          position: "absolute", left: Math.min(tip.clientX + 14, W - 160),
          top: Math.max(8, tip.clientY - 44),
          background: theme.text, color: "#fff", padding: "6px 10px",
          borderRadius: 6, fontSize: 10, fontFamily: fonts.mono,
          pointerEvents: "none", zIndex: 10, whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}>
          <div style={{ color: tip.val >= 0 ? "#6ee7b7" : "#fca5a5", fontWeight: 700 }}>
            {dollar(tip.val, 0)}
          </div>
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 9, marginTop: 2 }}>
            {tip.timestamp} · Trade #{tip.trade_id ?? tip.idx + 1}
          </div>
        </div>
      )}
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        <defs>
          <linearGradient id="tEqFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.12" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {grids.map((g, i) => (
          <g key={i}>
            <line x1={P.l} y1={g.y} x2={W - P.r} y2={g.y}
                  stroke={theme.divider} strokeWidth="1" />
            <text x={P.l - 8} y={g.y + 3.5} textAnchor="end"
                  fill={theme.text4} fontSize="10" fontFamily={fonts.mono}>
              {g.label}
            </text>
          </g>
        ))}
        {/* Zero-Line stärker hervorheben wenn mn < 0 */}
        {mn < 0 && (
          <line x1={P.l} y1={zeroY} x2={W - P.r} y2={zeroY}
                stroke={theme.text4} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
        )}
        <path d={`${d} L${last[0]},${zeroY} L${P.l},${zeroY} Z`} fill="url(#tEqFill)" />
        <path d={d} fill="none" stroke={color} strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={last[0]} cy={last[1]} r="4" fill={color}
                stroke="#fff" strokeWidth="2.5" />
        {/* Achsen-Beschriftung Ecken */}
        <text x={P.l} y={H - 8} fill={theme.text4} fontSize="9" fontFamily={fonts.mono}>
          {points[0].timestamp?.slice(0, 10)}
        </text>
        <text x={W - P.r} y={H - 8} textAnchor="end"
              fill={theme.text4} fontSize="9" fontFamily={fonts.mono}>
          {points[points.length - 1].timestamp?.slice(0, 10)}
        </text>
      </svg>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// EXIT-REASONS BAR CHART — horizontale Balken mit Count + PnL
// ═════════════════════════════════════════════════════════
export function ExitReasonsBar({ reasons }) {
  if (!reasons || reasons.length === 0) {
    return <div style={{ padding: 16, textAlign: "center",
                          color: theme.text4, fontSize: 12, fontStyle: "italic" }}>
      Keine Exit-Reasons
    </div>;
  }
  const maxCount = Math.max(...reasons.map(r => r.count || 0)) || 1;
  const totalTrades = reasons.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {reasons.map((r, i) => {
        const w = ((r.count || 0) / maxCount) * 100;
        const share = ((r.count || 0) / totalTrades * 100).toFixed(1);
        const pnlPositive = (r.total_pnl || 0) >= 0;
        return (
          <div key={i} style={{ display: "grid",
                                  gridTemplateColumns: "90px 1fr 110px",
                                  alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.text2,
                          fontFamily: fonts.mono }}>
              {r.reason}
            </div>
            <div style={{ position: "relative", height: 22,
                          background: theme.tint, borderRadius: 5,
                          overflow: "hidden" }}>
              <div style={{
                position: "absolute", top: 0, left: 0, bottom: 0,
                width: `${w}%`,
                background: pnlPositive
                  ? "linear-gradient(90deg, rgba(22,163,74,0.22), rgba(22,163,74,0.35))"
                  : "linear-gradient(90deg, rgba(220,38,38,0.18), rgba(220,38,38,0.32))",
                borderRadius: 5,
                transition: "width 0.4s ease-out",
              }} />
              <div style={{
                position: "absolute", top: 0, left: 8, right: 8, bottom: 0,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                fontSize: 10, fontFamily: fonts.mono, color: theme.text,
              }}>
                <span style={{ fontWeight: 700 }}>{r.count}</span>
                <span style={{ color: theme.text3 }}>{share}%</span>
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: fonts.mono,
                          color: pnlPositive ? theme.greenText : theme.redText,
                          textAlign: "right" }}>
              {dollar(r.total_pnl, 0)}
              <div style={{ fontSize: 9, color: theme.text4, fontWeight: 500 }}>
                WR {fmtPct(r.win_rate, 0)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// HOUR × WEEKDAY HEATMAP — 7×24 Grid, Farbe = avg_pnl
// ═════════════════════════════════════════════════════════
export function HourWeekdayHeatmap({ heatmap, W = 720, H = 210 }) {
  if (!heatmap || !heatmap.cells || heatmap.cells.length === 0) {
    return <div style={{ padding: 16, textAlign: "center",
                          color: theme.text4, fontSize: 12, fontStyle: "italic" }}>
      Keine Heatmap-Daten (zu wenig Trades oder fehlende Entry-Times)
    </div>;
  }
  const weekdayLabels = heatmap.weekday_labels || ["Mo","Di","Mi","Do","Fr","Sa","So"];

  // Sparse cells → lookup-map
  const cellMap = {};
  let maxAbsPnl = 0;
  let totalCount = 0;
  for (const c of heatmap.cells) {
    const key = `${c.weekday}_${c.hour}`;
    cellMap[key] = c;
    const abs = Math.abs(c.pnl || 0);
    if (abs > maxAbsPnl) maxAbsPnl = abs;
    totalCount += c.count || 0;
  }
  const pnlScale = maxAbsPnl || 1;  // normalize

  // Geometry
  const P = { t: 16, r: 12, b: 28, l: 36 };
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;
  const cellW = iw / 24;
  const cellH = ih / 7;

  // Tooltip state
  const [tip, setTip] = useState(null);
  const ref = useRef(null);

  // Color: grau wenn count=0, sonst green/red-gradient nach pnl-magnitude
  const colorForCell = cell => {
    if (!cell || !cell.count) return "rgba(0,0,0,0.03)";
    const intensity = Math.min(1, Math.abs(cell.pnl) / pnlScale);
    if (cell.pnl >= 0) {
      // grün skaliert
      return `rgba(22,163,74,${0.12 + intensity * 0.68})`;
    }
    return `rgba(220,38,38,${0.10 + intensity * 0.65})`;
  };

  const onCellEnter = (wd, h, cell, e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setTip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      weekday: weekdayLabels[wd],
      hour: h,
      cell,
    });
  };

  return (
    <div ref={ref} style={{ position: "relative" }}
         onMouseLeave={() => setTip(null)}>
      {tip && (
        <div style={{
          position: "absolute", left: Math.min(tip.x + 12, W - 180),
          top: Math.max(6, tip.y - 56),
          background: theme.text, color: "#fff", padding: "6px 10px",
          borderRadius: 6, fontSize: 10, fontFamily: fonts.mono,
          pointerEvents: "none", zIndex: 10, whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}>
          <div style={{ fontWeight: 700 }}>{tip.weekday} · {String(tip.hour).padStart(2,"0")}:00</div>
          {tip.cell ? (
            <>
              <div style={{ color: tip.cell.pnl >= 0 ? "#6ee7b7" : "#fca5a5", marginTop: 2 }}>
                {dollar(tip.cell.pnl, 0)} · {tip.cell.count} trades
              </div>
              <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 9 }}>
                Ø {dollar(tip.cell.avg_pnl, 2)} / trade
              </div>
            </>
          ) : (
            <div style={{ color: "rgba(255,255,255,0.5)", marginTop: 2 }}>Keine Trades</div>
          )}
        </div>
      )}
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        {/* Weekday-Labels (Y-Achse) */}
        {weekdayLabels.map((lbl, wd) => (
          <text key={wd}
                x={P.l - 6} y={P.t + wd * cellH + cellH / 2 + 3.5}
                textAnchor="end" fill={theme.text3} fontSize="10"
                fontFamily={fonts.mono} fontWeight="600">
            {lbl}
          </text>
        ))}
        {/* Stunden-Labels (X-Achse): jede 3. Stunde */}
        {[0, 3, 6, 9, 12, 15, 18, 21].map(h => (
          <text key={h}
                x={P.l + h * cellW + cellW / 2} y={P.t + ih + 16}
                textAnchor="middle" fill={theme.text4} fontSize="9"
                fontFamily={fonts.mono}>
            {String(h).padStart(2, "0")}
          </text>
        ))}
        {/* Cells */}
        {weekdayLabels.map((_, wd) =>
          Array.from({ length: 24 }, (_, h) => {
            const cell = cellMap[`${wd}_${h}`];
            const x = P.l + h * cellW;
            const y = P.t + wd * cellH;
            return (
              <rect key={`${wd}_${h}`}
                x={x + 0.5} y={y + 0.5}
                width={cellW - 1} height={cellH - 1}
                rx="1.5"
                fill={colorForCell(cell)}
                stroke={cell && cell.count ? "rgba(0,0,0,0.06)" : "transparent"}
                strokeWidth="1"
                onMouseEnter={e => onCellEnter(wd, h, cell, e)}
                style={{ cursor: cell ? "pointer" : "default" }}>
                {cell && cell.count > 0 && <title>{weekdayLabels[wd]} {String(h).padStart(2,"0")}:00 · {cell.count} trades · {dollar(cell.pnl,0)}</title>}
              </rect>
            );
          })
        )}
      </svg>
      {/* Legende */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginTop: 8, fontSize: 10, color: theme.text4,
                    fontFamily: fonts.mono }}>
        <span>{totalCount} Trades gebinnt</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: theme.redText }}>Verlust</span>
          <span style={{ display: "inline-flex", gap: 2 }}>
            {[0.8, 0.5, 0.25].map(a => <span key={a} style={{
              width: 12, height: 10, borderRadius: 2,
              background: `rgba(220,38,38,${a})`,
            }}/>)}
            <span style={{ width: 12, height: 10, borderRadius: 2, background: "rgba(0,0,0,0.05)" }}/>
            {[0.25, 0.5, 0.8].map(a => <span key={a} style={{
              width: 12, height: 10, borderRadius: 2,
              background: `rgba(22,163,74,${a})`,
            }}/>)}
          </span>
          <span style={{ color: theme.greenText }}>Gewinn</span>
        </span>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// MAE / MFE SCATTER — X=MAE (≤0), Y=MFE (≥0), Farbe=PnL-Sign
// ═════════════════════════════════════════════════════════
export function MaeMfeScatter({ pairs, W = 540, H = 360 }) {
  const [tip, setTip] = useState(null);
  const ref = useRef(null);

  if (!pairs || pairs.length === 0) {
    return <div style={{ padding: 16, textAlign: "center",
                          color: theme.text4, fontSize: 12, fontStyle: "italic" }}>
      Keine MAE/MFE-Daten. Dieser Run wurde vor dem Trade-Level-Deploy erstellt
      oder hat keine Trades mit MAE/MFE-Werten.
    </div>;
  }

  // Data bounds: MAE ≤ 0, MFE ≥ 0
  const maes = pairs.map(p => p.mae || 0);
  const mfes = pairs.map(p => p.mfe || 0);
  const maeMin = Math.min(0, ...maes);   // negativster Wert
  const mfeMax = Math.max(0, ...mfes);   // höchster MFE
  // Padding 5% auf beiden Achsen damit Punkte nicht auf Rand kleben
  const maeRange = (-maeMin) || 1;
  const mfeRange = mfeMax || 1;
  const xMin = maeMin - maeRange * 0.05;
  const xMax = 0 + maeRange * 0.05;
  const yMin = -mfeRange * 0.05;
  const yMax = mfeMax + mfeRange * 0.05;

  const P = { t: 16, r: 16, b: 34, l: 56 };
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;

  const xTo = v => P.l + ((v - xMin) / (xMax - xMin)) * iw;
  const yTo = v => P.t + ih - ((v - yMin) / (yMax - yMin)) * ih;

  // Gridlines: 4 vertikale (MAE), 4 horizontale (MFE)
  const xTicks = Array.from({ length: 5 }, (_, i) => xMin + ((xMax - xMin) * i) / 4);
  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);

  // Color per point
  const pointColor = p => {
    const pnl = Number(p.pnl) || 0;
    if (pnl > 0.01) return theme.green;
    if (pnl < -0.01) return theme.red;
    return theme.text4;
  };

  // Winner/Loser count for legend
  const winners = pairs.filter(p => (p.pnl || 0) > 0.01).length;
  const losers = pairs.filter(p => (p.pnl || 0) < -0.01).length;
  const be = pairs.length - winners - losers;

  // Diagonal-Referenzlinie: MFE = -MAE (Punkte oberhalb sind "bessere" Setups)
  const diagX1 = 0, diagY1 = 0;
  const diagX2 = Math.min(Math.abs(xMin), yMax);
  // In Datenraum: x2=-diagX2, y2=diagX2

  const onPointMove = (p, e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setTip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      p,
    });
  };

  return (
    <div ref={ref} style={{ position: "relative" }}
         onMouseLeave={() => setTip(null)}>
      {tip && (
        <div style={{
          position: "absolute", left: Math.min(tip.x + 12, W - 170),
          top: Math.max(6, tip.y - 70),
          background: theme.text, color: "#fff", padding: "6px 10px",
          borderRadius: 6, fontSize: 10, fontFamily: fonts.mono,
          pointerEvents: "none", zIndex: 10, whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}>
          <div style={{ fontWeight: 700 }}>Trade #{tip.p.trade_id ?? "?"}</div>
          <div style={{ color: tip.p.pnl >= 0 ? "#6ee7b7" : "#fca5a5" }}>
            PnL {dollar(tip.p.pnl, 0)}
          </div>
          <div style={{ color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
            MAE {dollar(tip.p.mae, 0)} · MFE {dollar(tip.p.mfe, 0)}
          </div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 9 }}>
            {tip.p.direction} · {tip.p.exit_reason}
          </div>
        </div>
      )}
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        {/* Gridlines + Tick-Labels */}
        {yTicks.map((v, i) => {
          const y = yTo(v);
          return (
            <g key={`y${i}`}>
              <line x1={P.l} y1={y} x2={W - P.r} y2={y}
                    stroke={theme.divider} strokeWidth="1" />
              <text x={P.l - 6} y={y + 3.5} textAnchor="end"
                    fill={theme.text4} fontSize="9" fontFamily={fonts.mono}>
                {dollar(v, 0)}
              </text>
            </g>
          );
        })}
        {xTicks.map((v, i) => {
          const x = xTo(v);
          return (
            <g key={`x${i}`}>
              <line x1={x} y1={P.t} x2={x} y2={P.t + ih}
                    stroke={theme.divider} strokeWidth="1" />
              <text x={x} y={P.t + ih + 14} textAnchor="middle"
                    fill={theme.text4} fontSize="9" fontFamily={fonts.mono}>
                {dollar(v, 0)}
              </text>
            </g>
          );
        })}
        {/* Axis-Labels */}
        <text x={P.l + iw / 2} y={H - 4} textAnchor="middle"
              fill={theme.text3} fontSize="10" fontWeight="600">
          MAE (adverse) →
        </text>
        <text transform={`rotate(-90) translate(${-(P.t + ih / 2)}, 14)`}
              textAnchor="middle"
              fill={theme.text3} fontSize="10" fontWeight="600">
          MFE (favorable) →
        </text>
        {/* Diagonal-Linie MFE = -MAE (1:1 RR reference) */}
        {diagX2 > 0 && (
          <line x1={xTo(0)} y1={yTo(0)}
                x2={xTo(-diagX2)} y2={yTo(diagX2)}
                stroke={theme.text4} strokeWidth="1"
                strokeDasharray="4 4" opacity="0.5" />
        )}
        {/* Datenpunkte */}
        {pairs.map((p, i) => (
          <circle key={i}
                  cx={xTo(p.mae || 0)} cy={yTo(p.mfe || 0)}
                  r="3.5"
                  fill={pointColor(p)}
                  fillOpacity="0.55"
                  stroke={pointColor(p)}
                  strokeWidth="1"
                  onMouseMove={e => onPointMove(p, e)}
                  style={{ cursor: "pointer" }}>
            <title>Trade #{p.trade_id} · PnL {dollar(p.pnl,0)} · MAE {dollar(p.mae,0)} · MFE {dollar(p.mfe,0)}</title>
          </circle>
        ))}
      </svg>
      {/* Legende */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginTop: 4, fontSize: 10, fontFamily: fonts.mono,
                    color: theme.text4 }}>
        <span style={{ display: "flex", gap: 12 }}>
          <span><span style={{ color: theme.greenText, fontWeight: 700 }}>●</span> Winner {winners}</span>
          <span><span style={{ color: theme.redText, fontWeight: 700 }}>●</span> Loser {losers}</span>
          {be > 0 && <span><span style={{ color: theme.text4, fontWeight: 700 }}>●</span> BE {be}</span>}
        </span>
        <span style={{ opacity: 0.7 }}>Gestrichelt: MFE = |MAE| (1:1 RR)</span>
      </div>
    </div>
  );
}
